import {
  AvailablePaymentCurrency,
  formatCurrencyAmount,
  isDrawingFormCategory,
  IsoLocale,
  Locales,
  TicketingFormCategory,
} from "@simplyk/common";

import { buildScheduleSummaryLabel, ScheduleSummaryTone } from "@/common/helpers/scheduleSummaryLabel";
import { toFiniteNumber } from "@/common/helpers/toFiniteNumber";
import { Translate } from "@/common/hooks/useTranslate";
import { RateData } from "@/features/LiveFormEditor/Steps/StepTickets/Rates/types";

export type ExtrasSummaryTone = ScheduleSummaryTone;

export type ExtrasSummaryIcon = "schedule" | "memberOnly";

export interface ExtrasSummaryPart {
  key: "schedule" | "group" | "memberOnly" | "taxReceipt" | "categories";
  text: string;
  tone: ExtrasSummaryTone;
  icon?: ExtrasSummaryIcon;
}

export type ExtrasSummaryRate = Pick<
  RateData,
  "isGroup" | "isScheduled" | "isMemberOnly" | "isGeneratingReceipt" | "receiptEligibleAmount" | "isPayWhatYouCan"
> & {
  attendeesByTicket: RateData["attendeesByTicket"] | string;
  categoryIds: string[] | undefined;
  scheduleStartDate: Date | string | null;
  scheduleEndDate: Date | string | null;
};

export interface ExtrasSummaryContext {
  t: Translate;
  locale: IsoLocale | Locales;
  currency: AvailablePaymentCurrency;
  timezone: string;
  now: Date;
  isRegistration: boolean;
  formCategory: TicketingFormCategory;
}

export const buildExtrasSummary = (
  rate: ExtrasSummaryRate,
  { t, locale, currency, timezone, now, isRegistration, formCategory }: ExtrasSummaryContext
): ExtrasSummaryPart[] => {
  const parts: ExtrasSummaryPart[] = [];

  const schedule = rate.isScheduled
    ? buildScheduleSummaryLabel({
        startDate: rate.scheduleStartDate,
        endDate: rate.scheduleEndDate,
        t,
        locale,
        timezone,
        now,
      })
    : null;
  if (schedule) {
    parts.push({ key: "schedule", text: schedule.text, tone: schedule.tone, icon: "schedule" });
  }

  const attendeesByTicket = toFiniteNumber(rate.attendeesByTicket);
  if (!isDrawingFormCategory(formCategory) && rate.isGroup && attendeesByTicket !== null && attendeesByTicket >= 2) {
    const guestsKey = isRegistration
      ? "ticketing.extrasSummary.includesGuests.registration"
      : "ticketing.extrasSummary.includesGuests";
    parts.push({ key: "group", text: t("dashboard", guestsKey, { count: attendeesByTicket }), tone: "neutral" });
  }

  if (rate.isMemberOnly) {
    parts.push({
      key: "memberOnly",
      text: t("dashboard", "ticketing.extrasSummary.memberOnly"),
      tone: "neutral",
      icon: "memberOnly",
    });
  }

  if (rate.isGeneratingReceipt && !rate.isPayWhatYouCan) {
    if (typeof rate.receiptEligibleAmount === "number" && rate.receiptEligibleAmount > 0) {
      parts.push({
        key: "taxReceipt",
        text: t("dashboard", "ticketing.extrasSummary.taxReceiptWithAmount", {
          amount: formatCurrencyAmount({ value: rate.receiptEligibleAmount / 100, locale, currency }),
        }),
        tone: "neutral",
      });
    } else {
      parts.push({
        key: "taxReceipt",
        text: t("dashboard", "ticketing.extrasSummary.taxReceiptNoAmount"),
        tone: "neutral",
        icon: undefined,
      });
    }
  }

  const categoriesCount = rate.categoryIds?.length ?? 0;
  if (categoriesCount > 0) {
    parts.push({
      key: "categories",
      text: t("dashboard", "ticketing.extrasSummary.categories", { count: categoriesCount }),
      tone: "neutral",
    });
  }

  return parts;
};
